/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { InitiativeCompany, InitiativePlain, InitiativeTags } from '../types/initiative';
import { isBefore } from './date';
import { SubscriptionService, ProductCodes } from '../services/SubscriptionService';
import { DataShareStatus } from '../types/dataShare';
import { CurrentUserData } from '../reducers/current-user';
import { ChildrenTree, Tree } from '../types/tree';

interface CompanyStatusResponse {
  onboarded: boolean;
  survey_created: boolean;
  survey_completed: boolean;
  data_shared: boolean;
  is_verified: boolean;
  is_premium: boolean | Boolean;
  status: 'active' | 'inactive' | 'invited';
}

export type CompanyStatusProps = Pick<InitiativeCompany,
  'userCount' |
  'lastCompletedSurveyDate' |
  'lastSurveyDate' |
  'requestedDataShares' |
  'customer' |
  'tags'
>;

export const getCompanyStatus = (company: CompanyStatusProps): CompanyStatusResponse => {

  const hasActiveSubscription = (
    SubscriptionService.hasActive(ProductCodes.CompanyTracker, company.customer?.subscriptions)
    ||
    SubscriptionService.hasActive(ProductCodes.SGXESGenome, company.customer?.subscriptions)
  );

  const requestedDataShares = company.requestedDataShares;
  const hasAcceptedDataShare = requestedDataShares?.some(s => s.status === DataShareStatus.Accepted);

  const cutoffDate = new Date();
  cutoffDate.setFullYear(new Date().getFullYear() - 2);

  const report = {
    onboarded: company.userCount > 0,
    survey_created: company.lastSurveyDate ? isBefore(company.lastSurveyDate, cutoffDate) : false,
    survey_completed: company.lastCompletedSurveyDate ? isBefore(company.lastCompletedSurveyDate, cutoffDate) : false,
    data_shared: Boolean(hasAcceptedDataShare),
    is_verified: company.tags?.includes(InitiativeTags.VerifiedOrganization) ?? false,
    is_premium: hasActiveSubscription,
  }

  const isActive =
    report.is_verified &&
    report.is_premium &&
    report.survey_created &&
    report.survey_completed &&
    report.data_shared

  return {
    ...report,
    status: isActive ? 'active' : report.onboarded ? 'inactive' : 'invited'
  };
}

export class InitiativeTree {
  static isDescendant(user: Pick<CurrentUserData, 'initiativeTree'>, descendantId: string, parentId: string, infiniteRecursionProtection: string[] = []): boolean {
    infiniteRecursionProtection.push(parentId);
    const initiativeTree = user.initiativeTree;
    if (!initiativeTree) {
      return false;
    }

    if (descendantId === parentId) {
      return true;
    }

    const initiative = initiativeTree.find(d => d._id === descendantId);
    if (!initiative?.parentId) {
      return false;
    }

    if (initiative.parentId === parentId) {
      return true;
    }

    if (infiniteRecursionProtection.includes(initiative.parentId)) {
      return false;
    }

    const parent = initiativeTree.find(d => d._id === initiative.parentId);
    if (!parent) {
      return false;
    }

    return this.isDescendant(user, parentId, parent._id, infiniteRecursionProtection);
  }
}

export const findRootParentInTree = (
  tree: Tree | ChildrenTree,
  id: string,
) => {
  if (tree.id === id) {
    return tree;
  }

  const rootParents = tree.children;

  if (!rootParents || rootParents.length === 0) {
    return tree;
  }

  for (let parent of rootParents) {
    const stack = [...parent.children ?? []];
    if (parent.id === id) {
      return parent;
    }
    while (stack && stack.length > 0) {
      const currentNode = stack.shift() as ChildrenTree;
      if (currentNode.id === id) {
        return parent;
      }
      currentNode?.children?.forEach((childNode) => stack.push(childNode));
    }
  }
  return;
}

export const findCurrentBranchInTree = (
  tree: Tree | ChildrenTree,
  id: string,
  branch: (Tree | ChildrenTree)[] = []
): { branch: (Tree | ChildrenTree)[]; node?: Tree | ChildrenTree } => {

  if (tree.id === id) {
    return { branch, node: tree };
  }

  if (tree.children && tree.children.length > 0) {
    const foundNode = tree.children.find((c) => {
      const found = findCurrentBranchInTree(c, id, branch);
      if (found.node) {
        found.branch.push(c);
      }

      return found.node;
    });
    return { branch, node: foundNode };
  }
  return { branch };
};

export const findAllChildrenById = (list: InitiativePlain[], parentId: string): InitiativePlain[] => {
  const descendants = [];

  const directChildren = list.filter((item) => item.parentId === parentId);

  for (const child of directChildren) {
    const childDescendants = findAllChildrenById(list, child._id);
    descendants.push(child, ...childDescendants);
  }

  return descendants;
};
